import React, { createContext, useContext, useMemo } from 'react';
import { flow, makeAutoObservable } from 'mobx';
import { ProductType } from './accessExpress.types';
import { useQuery } from 'react-query';
import {
  addOrderItems,
  clearAllOrderItems,
  getLastDraftOrder,
} from 'api/accessexpressv3/api';
import { DraftOrderAPIResponse } from 'api/accessexpressv3/schemas/OrdersSchemas';
import { ProductOfferingType } from 'screens/SelectServices/schemas';
import showToast from 'components/Toast/Toast';
import { toast } from 'react-toastify';
import { QuoteLocation } from 'api/accessexpress/schema';
import { ConfigRequirements } from './BaseComponents/Cart/locationFlow/access/ConfigRequirementsModal/schema';
import { getMNSPrice } from './utils';

export interface ICartItem {
  id?: string;
  category: string;
  subCategory?: string;
  name: string;
  price: number;
  quantity: number;
  outOfStock?: boolean;
  locationId?: string | number | null;
  item_details?: ProductOfferingType & {
    offering_id?: string;
    isOtherOption?: boolean;
    provider?: string;
  };
}

// export type AddressType = {
//   address_line_1?: string;
//   address_line_2?: string;
//   street?: string;
//   city: string;
//   state: string;
//   zip: string;
//   cw_site_id?: string;
//   name?: string;
//   site_name?: string;
//   macnum?: string;
//   parent_macnum?: string;
//   parent_name?: string;
//   local_contact_name?: string;
//   local_contact_phone?: string;
//   location_contact_email?: string;
//   shipping_method?: string;
//   business_name?: string;
//   location_name?: string;
//   billing_type?: number;
//   invoice_delivery_method?: number;
//   invoice_style?: string;
// };

// export type OrderItemType = {
//   type: string; //  # "access_service" or "mobility_device"
//   subtype: string; //  # "Broadband", "DIA", "Phone", etc.
//   pim_price_id?: string;
//   quantity: number;
//   location_id: string;
//   item_details: { [key: string]: string };
//   price?: number;
// };

// export type LocationType = {
//   address?: AddressType;
//   order_items?: OrderItemType[];
//   id?: string;
// };

// export type OrderType = {
//   id: string;
//   locations: LocationType[];
//   additional_details: {
//     bb_quote_request: string;
//     dia_quote_request: string;
//   };
//   order_name?: string;
//   // total_amount: number;
// };

export interface CartSiteAddress extends Omit<QuoteLocation, 'id'> {
  // Address ID
  id?: string | number;
  // Location ID
  external_id?: string;
  // only for checkout access!!!
  site_name?: string | null;
}

export class CartStore {
  private _isOpen = false;
  private _isEditingLocations = false;
  private _locations: Array<CartSiteAddress> = [];
  private _stagedLocations: Array<CartSiteAddress> = [];
  // this property is used to define what cart should be shown
  // default - cart with items or empty cart
  // locations - cart with locations form when click access product
  private _cartState: 'default' | 'locations' = 'default';
  loadingDraftQuote: boolean = false;
  loadingDraftForPricing: boolean = true;

  draftId: string | null = null;
  additionalDetails: DraftOrderAPIResponse['additional_details'] = null;
  locationActiveId?: string | number | null = null;
  items: ICartItem[] = [];
  selectedAccessProductCategory: ProductType['category'] | null = null;
  deletedItems: ICartItem[] = [];

  constructor() {
    makeAutoObservable(this);
  }

  init() {
    console.log('Cart Provider initialized');
  }

  get locations() {
    return this._isEditingLocations ? this._stagedLocations : this._locations;
  }

  get totalItems() {
    return this.items.length;
  }

  get totalQuantity() {
    const mobiltyQuantity = this.items.reduce(
      (sum, item) => sum + (item.category === 'Mobility' ? item.quantity : 0),
      0,
    );
    const accessQuantity = this.items.reduce(
      (sum, item) => sum + (item.category === 'Access' ? item.quantity : 0),
      0,
    );
    const totalQuantity = mobiltyQuantity + accessQuantity;
    return totalQuantity;
  }

  get isOpen() {
    return this._isOpen;
  }

  get cartState() {
    return this._cartState;
  }

  get isEditingLocations() {
    return this._isEditingLocations;
  }

  get activeLocation() {
    return this._locations.find((l) => l?.id === this.locationActiveId) ?? null;
  }

  get subTotal() {
    const total = this.items.reduce(
      (sum, item) =>
        sum + (item.price + getMNSPrice(item.item_details!)) * item.quantity,
      0,
    );
    return total.toFixed(2);
  }

  get total() {
    const total = this.items.reduce(
      (sum, item) =>
        sum + (item.price + getMNSPrice(item.item_details!)) * item.quantity,
      0,
    );
    return total.toFixed(2);
  }

  get locationMissingServices() {
    const _locations: Array<CartSiteAddress> = [];
    this._locations.forEach((location) => {
      if (!this.items.some((item) => item.locationId === location.id))
        _locations.push(location);
    });
    return _locations;
  }

  setCartState = (state: 'default' | 'locations') => {
    this._cartState = state;
  };
  get quoteId() {
    return (
      this.additionalDetails?.bb_quote_request ||
      this.additionalDetails?.dia_quote_request
    );
  }

  removeLocationsMissingServices = () => {
    const missingLocationIds = this.locationMissingServices.map(
      (loc) => loc.id,
    );

    this._locations = this._locations.filter(
      (loc) => !missingLocationIds.includes(loc.id),
    );

    if (missingLocationIds.includes(this.locationActiveId as string)) {
      this.locationActiveId = this._locations[0]?.id ?? null;
    }
  };

  itemsByLocation = (locationId: ICartItem['locationId']) => {
    return this.items.filter((item) => {
      return item.locationId === locationId;
    });
  };

  itemsByCategory(category: ICartItem['category']) {
    return this.items.filter((item) => item.category === category);
  }

  setLoadingDraftQuote = (loadingDraftQuote: boolean) => {
    this.loadingDraftQuote = loadingDraftQuote;
    this.loadingDraftForPricing = loadingDraftQuote;
  };

  setLocationActiveId(locationId: CartSiteAddress['id'] | null) {
    this.locationActiveId = locationId;
  }

  goNextLocation = () => {
    if (!this.locationActiveId || this._locations.length === 0) return;

    const currentIndex = this._locations.findIndex(
      (loc) => loc.id === this.locationActiveId,
    );

    if (currentIndex !== -1 && currentIndex < this._locations.length - 1) {
      this.locationActiveId = this._locations[currentIndex + 1].id;
    }
  };

  goPrevLocation = () => {
    if (!this.locationActiveId || this._locations.length === 0) return;

    const currentIndex = this._locations.findIndex(
      (loc) => loc.id === this.locationActiveId,
    );

    if (currentIndex > 0) {
      this.locationActiveId = this._locations[currentIndex - 1].id;
    }
  };

  updateStateFromDraft = (draft: DraftOrderAPIResponse) => {
    const _locations: Array<CartSiteAddress> = [];
    const _items: Array<ICartItem> = [];

    (draft?.locations || []).forEach((location) => {
      _locations.push({
        ...location.address,
        external_id: location.id,
        address_line_1: location.address?.address_line_1 || '',
        city: location.address?.city || '',
        name: location.address?.name || '',
        state: location.address?.state || '',
        zip: location.address?.zip || '',
        id: location?.id || '',
      });
      location.order_items.forEach((item) =>
        _items.push({
          ...item,
          id: item.id,
          category: item.type === 'access_service' ? 'Access' : item.type,
          subCategory: item.subtype,
          name:
            item.subtype === 'Broadband' ? 'Broadband circuit' : 'DIA circuit',
          price: item.price as number,
          quantity: item.quantity,
          outOfStock: false,
          locationId: location.id,
          item_details: item.item_details,
        }),
      );
    });
    // update store one time !!!
    this.additionalDetails = draft.additional_details ?? null;

    this._locations = _locations;

    this.items = _items;

    this.draftId = draft.id;

    this.locationActiveId = _locations[0]?.id ?? null;

    if (_items.length) {
      this._isOpen = true;
    }
  };

  updateLocationOrderItems = flow(function* (
    this: CartStore,
    item: ProductOfferingType,
    cartItem: ICartItem,
  ) {
    const offeringId = (item.formal_broadband_product?.id ||
      item.formal_dia_product?.id) as string;
    try {
      const items = {
        order_items: this.items
          .filter((orderItem) => orderItem.locationId === this.locationActiveId)
          .map((orderItem) => {
            return {
              ...(orderItem.item_details?.offering_id ===
              cartItem.item_details?.offering_id
                ? {
                    ...cartItem,
                    item_details: {
                      ...item,
                      offering_id: offeringId,
                    },
                  }
                : orderItem),

              type: 'access_service',
              subtype: 'Broadband',
              location_id: this.locationActiveId as string,
            };
          }),
      };
      yield addOrderItems(items, this.locationActiveId as string);
    } catch {
      console.error('');
    }
  });

  deleteProductFromOrder = flow(function* (
    this: CartStore,
    offeringId: string,
  ) {
    try {
      const items = {
        order_items: this.items
          .filter((orderItem) => orderItem.locationId === this.locationActiveId)
          .map((orderItem) => {
            if (orderItem.item_details?.offering_id !== offeringId) {
              return {
                ...orderItem,
                type: 'access_service',
                subtype: 'Broadband',
                location_id: this.locationActiveId as string,
              };
            }
            return null;
          })
          .filter(Boolean),
      };
      //@ts-expect-error err
      return yield addOrderItems(items, this.locationActiveId as string);
    } catch {
      console.error('');
    }
  });

  clearOrderItems = flow(function* (this: CartStore) {
    try {
      return yield clearAllOrderItems(this.draftId!);
    } catch {
      console.error('');
    }
  });

  reset = () => {
    this._isOpen = false;
    this._isEditingLocations = false;
    this._locations = [];
    this.loadingDraftQuote = false;
    this.draftId = null;
    this.additionalDetails = null;
    this.locationActiveId = null;
    this.items = [];
    this.selectedAccessProductCategory = null;
    this.deletedItems = [];
  };

  setLocations(newLocations: Array<CartSiteAddress>) {
    if (this._isEditingLocations) {
      this._stagedLocations = newLocations;
    } else {
      this._locations = newLocations;
    }
  }

  setConfigs = (configs: ConfigRequirements) => {
    this.additionalDetails = {
      ...this.additionalDetails,
      configs,
    };
  };

  setOrderItems(orderItems: Array<ICartItem>) {
    this.items = orderItems;
  }

  editLocations() {
    this._isEditingLocations = true;
    this._stagedLocations = [...this._locations];
  }

  cancelEditingLocations() {
    this._isEditingLocations = false;
    this._stagedLocations = [];
  }

  doneEditingLocations = () => {
    this._locations = [...this._stagedLocations];
    this._isEditingLocations = false;
    this._stagedLocations = [];
  };

  open() {
    // this.selectedAccessProductCategory = null;
    this._isOpen = true;
  }

  close() {
    // todo:think this cancelEditingLocations need to be here!
    // this.cancelEditingLocations();
    this._isOpen = false;
    setTimeout(() => {
      this._cartState = 'default';
    }, 400);
  }

  selectAccessProductCategory(productCategory: ProductType['category']) {
    this.open();
    this._cartState = 'locations';
    this.selectedAccessProductCategory = productCategory;
  }

  clearAll() {
    this.items = [];
  }

  checkout() {}

  removeItem(id: ICartItem['id']) {
    this.items = this.items.filter((item) => item.id !== id);
  }

  updateItemQuantity(id: ICartItem['id'], newQuantity: number) {
    if (newQuantity < 1) return;
    this.items = this.items.map((item) =>
      item.id === id ? { ...item, quantity: newQuantity } : item,
    );
  }

  addItem = (item: ICartItem) => {
    this.open();
    // update draft products for specific location on BE
    const _item = { ...item, locationId: this.locationActiveId };
    const existingItem = this.items.find((cartItem) => cartItem.id === item.id);
    if (existingItem) {
      const samePrice = existingItem.price === item.price;
      if (!samePrice) {
        existingItem.price = item.price;
        existingItem.quantity = item.quantity;
      } else existingItem.quantity += item.quantity;
    } else {
      this.items = [...this.items, _item];
    }
  };

  hasCircuitLimitReached = (
    subCategory: ICartItem['subCategory'],
    offeringId: string,
  ): boolean => {
    const locId = this.locationActiveId;
    if (!locId) return false;

    const circuits = this.items.filter(
      (item) =>
        item.locationId === locId &&
        item.subCategory === subCategory &&
        // don't disable items buttons that are already added to cart
        item.item_details?.offering_id !== offeringId,
    );

    const uniqueCombos = new Set(
      circuits.map((item) => item.item_details?.provider),
    );

    return uniqueCombos.size >= 2;
  };

  getCircuitsByProductTypeAndLocation = (
    productType: ICartItem['subCategory'],
    locationId: string | number,
  ) => {
    return this.items.filter(
      (item) =>
        item.subCategory === productType && item.locationId === locationId,
    );
  };

  hasProviderCombo = (provider: string, locationId: string | number) => {
    return this.items.some(
      (item) =>
        item.locationId === locationId &&
        item.item_details?.provider === provider,
    );
  };

  canAddCircuitToLocation = (
    item: ICartItem,
    provider: string,
  ): {
    valid: boolean;
    reason?: string;
  } => {
    const locationId = item.locationId ?? this.locationActiveId;
    if (!locationId) return { valid: false };

    const circuits = this.getCircuitsByProductTypeAndLocation(
      item.subCategory!,
      locationId,
    );

    if (circuits.length >= 2) {
      return {
        valid: false,
        reason:
          "You may only add two broadband circuits to each location's cart.",
      };
    }

    const hasCombo = this.hasProviderCombo(provider ?? '', locationId);
    if (hasCombo) {
      return {
        valid: false,
        reason:
          'Access service with this vendor already added for this location!',
      };
    }

    return { valid: true };
  };

  isItemAlreadyInCart = (id: string): boolean => {
    return this.items.some(
      (item) =>
        item.locationId === this.locationActiveId &&
        item.item_details?.offering_id === id,
    );
  };

  addAccessCartItem = async (
    item: ProductOfferingType & {
      isOtherOption?: boolean;
      mns_essential?: boolean;
      mns_enhanced?: boolean;
    },
  ): Promise<{ success: boolean; reason?: string }> => {
    const locationId = this.locationActiveId;
    if (!locationId) return { success: false };

    const offeringId = (item.formal_broadband_product?.id ||
      item.formal_dia_product?.id) as string;

    const cartItem = {
      category: 'Access',
      subCategory: 'Broadband',
      name: 'Broadband circuit',
      price:
        (item.formal_broadband_product?.formal_broadband_pricing?.total_mrc ||
          item.formal_dia_product?.formal_dia_pricing?.total_mrc) ??
        0,
      quantity: 1,
      outOfStock: false,
      locationId: this.locationActiveId,
      item_details: {
        // need to check with Eduana - write by met!
        // need to pass item details too!
        ...item,
        provider: item.provider,
        offering_id: offeringId,
        isOtherOption: item.isOtherOption,
        mns_essential: item.mns_essential,
        mns_enhanced: item.mns_enhanced,
      },
    };

    const validation = this.canAddCircuitToLocation(cartItem, item.provider);

    if (!validation.valid) {
      showToast.error({ message: validation.reason ?? '', autoClose: true });
      // Don't add item
      return { success: false, reason: validation.reason };
    }

    const newItem = { ...cartItem, locationId };
    this.items = [...this.items, newItem];
    this.open();
    await this.updateLocationOrderItems(item, cartItem);
    return { success: true };
  };

  removeAccessCartItem = async (id: string) => {
    const locationId = this.locationActiveId;
    if (!locationId) return { success: false };

    await this.deleteProductFromOrder(id);
    this.items = this.items.filter(
      (item) => item.item_details?.offering_id !== id,
    );
  };

  removeMobilityCartItem = async (id: string) => {
    this.items = this.items.filter((item) => item.id !== id);
  };

  resetAccessItems = () => {
    this.items = this.deletedItems;
    this.deletedItems = [];
  };

  clearAllAccessItems = async () => {
    let undoClicked = false;
    this.deletedItems = [...this.items];
    this.items = [];

    const toastId = showToast.confirmation({
      message: 'Cart cleared!',
      buttonLabel: 'Undo',
      onClick: () => {
        undoClicked = true;
        this.resetAccessItems();
        toast.dismiss(toastId);
      },
    });

    toast.onChange((payload) => {
      if (
        payload.status === 'removed' &&
        payload.id === toastId &&
        !undoClicked
      ) {
        this.clearOrderItems();
      }
    });
  };
}

const CartContext = createContext<CartStore | null>(null);

export const CartContextProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const store = useMemo(() => new CartStore(), []);

  useQuery([], getLastDraftOrder, {
    refetchOnWindowFocus: false,
    onSuccess: (data) => {
      if (data) {
        if (data.locations?.length)
          store.setLocationActiveId(data.locations[0].id);
        store.updateStateFromDraft(data);
      }
    },
    onSettled: () => {
      store.setLoadingDraftQuote(false);
    },
  });

  return <CartContext.Provider value={store}>{children}</CartContext.Provider>;
};

export const useCartContext = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCartStore must be used within a CartProvider');
  }
  return context;
};
